import { QuestionType } from "../../types/question";
import { DEMOGRAPHIC_GROUP_IDS } from "../../types/report";
import {
  QuestionBreakdown,
  QuestionBreakdownDemographicGroup,
  QuestionBreakdownOptions,
} from "../../types/workplaceCulture";
import { getQuestionDemographicGroups } from "../../utils/QuestionBreakdown.utils";

// Option IDs that should show full demographic breakdown charts
// Based on the data: Microaggressions, Toxic work environment, and Discrimination
const CHART_OPTION_IDS = ["OPT.0080", "OPT.0083", "OPT.0084"];

export interface ProcessedQuestion extends QuestionBreakdown {
  isMultiSelect: boolean;
  showChart?: boolean;
  optionData?: QuestionBreakdownOptions;
  originalQuestionText?: string;
}

export interface GroupedCountItems {
  questionText: string;
  items: ProcessedQuestion[];
}

export const processQuestionsForDisplay = (
  questions: QuestionBreakdown[]
): {
  chartItems: ProcessedQuestion[];
  countItems: ProcessedQuestion[];
  groupedCountItems: GroupedCountItems[];
} => {
  const allItems: ProcessedQuestion[] = questions.flatMap((question) => {
    const isMultiSelect = question.kind === QuestionType.MultiSelect;

    // Find the marginalised people demographic group (DEG.100)
    const marginalisedGroup = question.demographic_groups.find(
      (group) =>
        group.reference_id === DEMOGRAPHIC_GROUP_IDS.MARGINALISED_PEOPLE
    );

    // If marginalised group doesn't exist, skip this question
    if (!marginalisedGroup) {
      return [];
    }

    // For multi-select questions, expand into separate questions for each option
    if (isMultiSelect) {
      // Get individual options from total_responses, filter out skip options
      const options = (question.total_responses?.options || []).filter(
        (opt) => !opt.is_skip
      );

      if (options.length === 0) {
        return [];
      }

      // Map each option to a separate rendered question
      return options.map((option) => {
        const shouldShowChart = CHART_OPTION_IDS.includes(
          option.reference_id || ""
        );

        if (shouldShowChart) {
          // Filter demographic groups for this specific option
          // This will filter combined_options to only include those with this option_id
          const filteredDemographicGroups = getQuestionDemographicGroups(
            question.demographic_groups,
            option.reference_id || ""
          );

          const expandedQuestion: QuestionBreakdown = {
            text: option.text,
            reference_id: option.reference_id || "",
            kind: question.kind,
            demographic_groups: filteredDemographicGroups,
            total_responses: question.total_responses,
            preamble: question.preamble,
            clarification: question.clarification,
          };

          return {
            ...expandedQuestion,
            isMultiSelect: true,
            showChart: true,
            optionData: option,
            originalQuestionText: question.text, // Store original question text
          };
        } else {
          // Just show the number for this option
          return {
            text: option.text,
            reference_id: option.reference_id || "",
            kind: question.kind,
            demographic_groups: [],
            total_responses: question.total_responses,
            preamble: question.preamble,
            clarification: question.clarification,
            isMultiSelect: true,
            showChart: false,
            optionData: option,
            originalQuestionText: question.text, // Store original question text
          };
        }
      });
    }

    // For non-multi-select questions, return as-is
    return [{ ...question, isMultiSelect: false } as ProcessedQuestion];
  });

  // Separate items into chart-enabled and count-only
  const chartItems = allItems.filter((item) => item.showChart !== false);
  const countItems = allItems.filter((item) => item.showChart === false);

  // Group count items by original question text
  const groupedCountItems: GroupedCountItems[] = [];
  const questionGroups = new Map<string, ProcessedQuestion[]>();

  countItems.forEach((item) => {
    const processedItem = item as ProcessedQuestion;
    const questionText =
      processedItem.originalQuestionText || processedItem.text;
    if (!questionGroups.has(questionText)) {
      questionGroups.set(questionText, []);
    }
    const group = questionGroups.get(questionText);
    if (group) {
      group.push(processedItem);
    }
  });

  questionGroups.forEach((items, questionText) => {
    // Sort items by reference_id (option_id)
    const sortedItems = [...items].sort((a, b) => {
      const idA = a.reference_id || "";
      const idB = b.reference_id || "";
      return idA.localeCompare(idB);
    });
    groupedCountItems.push({ questionText, items: sortedItems });
  });

  return { chartItems, countItems, groupedCountItems };
};

export const findMarginalisedGroup = (
  demographicGroups: QuestionBreakdownDemographicGroup[]
): QuestionBreakdownDemographicGroup | undefined => {
  return demographicGroups.find(
    (group) =>
      group.reference_id === DEMOGRAPHIC_GROUP_IDS.MARGINALISED_PEOPLE ||
      group.label === DEMOGRAPHIC_GROUP_IDS.MARGINALISED_PEOPLE
  );
};

/**
 * Format percentage with count for display
 * @param count - The count value
 * @param engagement - The total engagement value
 * @returns Formatted string like "10% (N=5)"
 */
export const formatPercentageWithCount = (
  count: number,
  engagement: number
): string => {
  // Handle invalid inputs
  if (Number.isNaN(count) || Number.isNaN(engagement)) {
    return `N/A (N=${count})`;
  }

  // Handle negative numbers
  if (count < 0 || engagement < 0) {
    return `N/A (N=${count})`;
  }

  // Handle division by zero
  if (engagement === 0) {
    return count === 0 ? "0% (N=0)" : `N/A (N=${count})`;
  }

  // Calculate percentage
  const percentage = (count / engagement) * 100;

  // Cap at 100% and show warning for data integrity issues
  if (percentage > 100) {
    return `100% (N=${count})`;
  }

  return `${percentage.toFixed(0)}% (N=${count})`;
};

/**
 * Create marginalised demographic group structure with proper defaults
 * @param marginalisedGroup - The marginalised group to transform
 * @returns QuestionBreakdownDemographicGroup with proper structure
 */
export const createMarginalisedDemographicGroup = (
  marginalisedGroup: QuestionBreakdownDemographicGroup
): QuestionBreakdownDemographicGroup => ({
  label: marginalisedGroup.label,
  demographics: marginalisedGroup.demographics || [],
  is_custom: marginalisedGroup.is_custom || false,
  is_combined: marginalisedGroup.is_combined,
  reference_id: marginalisedGroup.reference_id || marginalisedGroup.label,
});
